// Site Visit Details Page

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  Container,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Avatar,
  Stack,
  Alert,
  CircularProgress,
  Badge,
  Tooltip,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DirectionsCar as CarIcon,
  LocalTaxi as TaxiIcon,
  DirectionsWalk as WalkIcon,
  DirectionsBus as BusIcon,
  Videocam as VideoIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Event as EventIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  ContactSupport as ContactSupportIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useSiteVisit } from '../../contexts/SiteVisitContext';
import { getSiteVisitStatusColor, getSiteVisitTravelTypeColor } from '../../utils/helper';
import TableBadge from '../../components/common/TableBadge';
import SiteVisitActions from './SiteVisitActions';


// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const SiteVisitDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fetchSiteVisitDetail } = useSiteVisit();
  const [siteVisit, setSiteVisit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');


  useEffect(() => {
    const fetchSiteVisit = async () => {
      if (!id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetchSiteVisitDetail(id);
        setSiteVisit(response);
      } catch (error) {
        console.error('Error fetching site visit:', error);
        setError('Failed to load site visit details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSiteVisit();
  }, [id]);



  const handleBack = () => {
    navigate('/site-visits');
  };







  const handleRefresh = () => {
    const fetchSiteVisit = async () => {
      if (!id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetchSiteVisitDetail(id);
        setSiteVisit(response);
      } catch (error) {
        console.error('Error fetching site visit:', error);
        setError('Failed to load site visit details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSiteVisit();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Set';
    return dayjs(dateString).tz('Asia/Kolkata').format('MMM DD, YYYY');
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Not Set';
    return dayjs(dateString).tz('Asia/Kolkata').format('h:mm A');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not Set';
    return dayjs(dateString).tz('Asia/Kolkata').format('MMM DD, YYYY - h:mm A');
  };

  const getTransportIcon = (transportType) => {
    switch (transportType) {
      case 'self':
        return <CarIcon fontSize="small" />;
      case 'cab':
        return <TaxiIcon fontSize="small" />;
      case 'walk':
        return <WalkIcon fontSize="small" />;
      case 'bus':
        return <BusIcon fontSize="small" />;
      case 'virtual':
        return <VideoIcon fontSize="small" />;
      default:
        return <CarIcon fontSize="small" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled':
        return <ScheduleIcon color="primary" />;
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'rescheduled':
        return <EventIcon color="warning" />;
      default:
        return <InfoIcon color="action" />;
    }
  };

  const getInterestLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high':
        return { color: '#d32f2f', background: '#ffebee', border: '#f44336' };
      case 'medium':
        return { color: '#f57c00', background: '#fff3e0', border: '#ff9800' };
      case 'low':
        return { color: '#388e3c', background: '#e8f5e8', border: '#4caf50' };
      default:
        return { color: '#757575', background: '#f5f5f5', border: '#9e9e9e' };
    }
  };

  // Get rescheduling history from API data and add current visit
  const visitHistory = siteVisit?.visit_history || [];
  const currentVisit = {
    id: siteVisit?.id,
    status: siteVisit?.status,
    status_verbose: siteVisit?.status_verbose,
    visit_type: siteVisit?.visit_type,
    visit_type_verbose: siteVisit?.visit_type_verbose,
    transportation_type_verbose: siteVisit?.transportation_type_verbose,
    calendar_item: {
      title: siteVisit?.calendar_item?.title,
      start_time: siteVisit?.calendar_item?.start_time,
      end_time: siteVisit?.calendar_item?.end_time,
      agent: siteVisit?.agent?.name
    },
    agent_notes: siteVisit?.agent_notes,
    rescheduling_reason: siteVisit?.rescheduling_reason
  };
  
  // Combine history with current visit (current visit first)
  const reschedulingHistory = [currentVisit, ...visitHistory];

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading site visit details...
            </Typography>
          </Stack>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back to Site Visits
        </Button>
      </Container>
    );
  }

  if (!siteVisit) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Site visit not found
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back to Site Visits
        </Button>
      </Container>
    );
  }

  // Ensure all required data is available before rendering
  if (!siteVisit.id || !siteVisit.lead || !siteVisit.agent) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const leadInfo = siteVisit.lead;
  const agentInfo = siteVisit.agent;
  const projectInfo = siteVisit.project;
  const transportInfo = getSiteVisitTravelTypeColor(siteVisit.transportation_type);
  const statusInfo = getSiteVisitStatusColor(siteVisit.status);

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Row 1: Action Buttons - Right Side */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        {siteVisit && (
          <Fade in={!!siteVisit} timeout={800}>
            <div>
              <SiteVisitActions
                siteVisit={siteVisit}
                mode="details"
              />
            </div>
          </Fade>
        )}
      </Box>

      {/* Row 2: Two Column Grid Layout */}
      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Left Column - 70% */}
        <Grid item xs={12} lg={8.4}>
          <Stack spacing={3} sx={{ height: '100%', minHeight: '600px' }}>
            {/* Row 1: Lead and Visit Related Data */}
            <Zoom in={!!siteVisit} timeout={1000}>
              <Card sx={{ borderRadius: 4, boxShadow: 4, overflow: 'hidden' }}>
                <CardHeader
                  title="Lead & Visit Information"
                  avatar={<PersonIcon color="primary" />}
                  titleTypographyProps={{ fontWeight: 700, fontSize: '1.25rem' }}
                  sx={{ 
                    pb: 2,
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    borderBottom: '1px solid #e2e8f0'
                  }}
                />
                <CardContent sx={{ p: 1.5 }}>
                  <Grid container spacing={1.5}>
                    {/* Lead Information Section */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ 
                        p: 1.5, 
                        bgcolor: 'grey.50', 
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        height: '100%'
                      }}>
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 1.5 }}>
                          Lead Information
                        </Typography>
                        
                        {/* Lead Profile */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor: 'primary.main',
                              fontSize: '1.2rem',
                              fontWeight: 600,
                              boxShadow: 2
                            }}
                          >
                            {leadInfo?.name?.charAt(0)?.toUpperCase() || 'L'}
                          </Avatar>
                          <Box>
                            <Button
                              variant="text"
                              sx={{ 
                                p: 0, 
                                textTransform: 'none', 
                                textAlign: 'left',
                                justifyContent: 'flex-start',
                                minWidth: 'auto',
                                '&:hover': { bgcolor: 'transparent' }
                              }}
                              onClick={() => navigate(`/leads/${leadInfo?.id}`)}
                            >
                              <Typography variant="h6" fontWeight={600} color="primary.main">
                                {leadInfo?.name || 'Lead Name'}
                              </Typography>
                            </Button>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                              #{leadInfo?.lead_id || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Contact Information */}
                        <Stack spacing={1.5}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5, 
                            p: 1.5, 
                            bgcolor: 'white', 
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: 2,
                              transform: 'translateY(-1px)'
                            }
                          }}>
                            <Box sx={{ 
                              p: 1, 
                              bgcolor: 'primary.main', 
                              borderRadius: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <PhoneIcon sx={{ color: 'white', fontSize: 18 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Phone Number
                              </Typography>
                              <Typography variant="body1" fontWeight={600} color="text.primary">
                                {leadInfo?.mobile || 'Not Available'}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5, 
                            p: 1.5, 
                            bgcolor: 'white', 
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: 2,
                              transform: 'translateY(-1px)'
                            }
                          }}>
                            <Box sx={{ 
                              p: 1, 
                              bgcolor: 'primary.main', 
                              borderRadius: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <EmailIcon sx={{ color: 'white', fontSize: 18 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Email Address
                              </Typography>
                              <Typography variant="body1" fontWeight={600} color="text.primary">
                                {leadInfo?.email || 'Not Available'}
                              </Typography>
                            </Box>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                    
                    {/* Visit Information Section */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ 
                        p: 1.5, 
                        bgcolor: 'grey.50', 
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        height: '100%'
                      }}>
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 1.5 }}>
                          Visit Information
                        </Typography>
                        
                        <Stack spacing={1.5}>
                          {/* Scheduled Visit */}
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'white', 
                            borderRadius: 2,
                            border: '1px solid #e2e8f0'
                          }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>
                              Scheduled Visit
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 0.25 }}>
                              {siteVisit.calendar_item?.date_verbose || formatDate(siteVisit.calendar_item?.start_time)}
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                              {siteVisit.calendar_item?.start_time_verbose || formatTime(siteVisit.calendar_item?.start_time)}
                            </Typography>
                            {siteVisit.calendar_item?.end_time && (
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                p: 0.5, 
                                bgcolor: 'grey.50', 
                                borderRadius: 1,
                                display: 'inline-block',
                                fontSize: '0.7rem'
                              }}>
                                Duration: {formatTime(siteVisit.calendar_item.start_time)} - {formatTime(siteVisit.calendar_item.end_time)}
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Completion Information */}
                          {siteVisit.status === 'completed' && siteVisit.calendar_item?.completed_at && (
                            <Box sx={{ 
                              p: 1.5, 
                              bgcolor: 'success.50', 
                              borderRadius: 1.5, 
                              border: '1px solid #4caf50',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                bgcolor: 'success.main'
                              }} />
                              <Typography variant="caption" color="success.dark" gutterBottom sx={{ fontWeight: 600, mb: 0.25 }}>
                                ✅ Completed
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="success.main">
                                {siteVisit.calendar_item.completed_at}
                              </Typography>
                              {siteVisit.calendar_item?.completion_note && (
                                <Typography variant="caption" color="success.dark" sx={{ mt: 0.25, fontStyle: 'italic', fontSize: '0.7rem' }}>
                                  Note: {siteVisit.calendar_item.completion_note}
                                </Typography>
                              )}
                            </Box>
                          )}
                          
                          {/* Visit Details */}
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'white', 
                            borderRadius: 2,
                            border: '1px solid #e2e8f0'
                          }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>
                              Visit Types & Transportation
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <TableBadge
                                label={siteVisit.visit_type_verbose || siteVisit.visit_type || 'Unknown'}
                                color="#1976d2"
                                background="#e3f2fd"
                                border="#bbdefb"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                {transportInfo.icon && React.cloneElement(transportInfo.icon, { fontSize: 'small' })}
                                <TableBadge
                                  label={siteVisit.transportation_type_verbose || siteVisit.transportation_type || 'Unknown'}
                                  color={transportInfo.color}
                                  background={transportInfo.background}
                                  border={transportInfo.color}
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Row 2: Project Information */}
            {projectInfo && (
              <Fade in={!!siteVisit} timeout={1200}>
                <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                  <CardHeader
                    title="Project Information"
                    avatar={<HomeIcon color="primary" fontSize="small" />}
                    titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  />
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack spacing={2}>
                      {/* Project Details */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Project
                            </Typography>
                            <Button
                              variant="text"
                              sx={{ 
                                p: 0,
                                textTransform: 'none', 
                                textAlign: 'left',
                                justifyContent: 'flex-start',
                                minWidth: 'auto',
                                '&:hover': { bgcolor: 'transparent' }
                              }}
                              onClick={() => navigate(`/projects/${projectInfo.id}`)}
                            >
                              <Typography variant="body1" fontWeight={600} color="primary.main">
                                {projectInfo.name}
                              </Typography>
                            </Button>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          {projectInfo.developer && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Developer
                              </Typography>
                              <Button
                                variant="text"
                                sx={{ 
                                  p: 0, 
                                  textTransform: 'none', 
                                  textAlign: 'left',
                                  justifyContent: 'flex-start',
                                  minWidth: 'auto',
                                  '&:hover': { bgcolor: 'transparent' }
                                }}
                                onClick={() => navigate(`/developers/${projectInfo.developer.id}`)}
                              >
                                <Typography variant="body1" fontWeight={600} color="primary.main">
                                  {projectInfo.developer.name}
                                </Typography>
                              </Button>
                            </Box>
                          )}
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Address
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5, fontSize: 16 }} />
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {projectInfo.address}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {projectInfo.city}, {projectInfo.state} - {projectInfo.pincode}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Configurations & Units */}
                      {siteVisit.visit_units && siteVisit.visit_units.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Configurations & Units
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {siteVisit.visit_units.map((visitUnit, index) => (
                                                              <Chip
                                  key={index}
                                  label={`${visitUnit.configuration?.name || visitUnit.configuration}${visitUnit.unit?.name || visitUnit.unit ? `, ${visitUnit.unit?.name || visitUnit.unit}` : ''}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ 
                                    borderRadius: 2,
                                    fontSize: '0.7rem',
                                    '& .MuiChip-label': { 
                                      px: 1,
                                      fontWeight: 500
                                    }
                                  }}
                                />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            )}

            {/* Row 3: Notes and Miscellaneous */}
            <Fade in={!!siteVisit} timeout={1400}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardHeader
                  title="Notes & Additional Information"
                  avatar={<AssignmentIcon color="primary" fontSize="small" />}
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                />
                <CardContent sx={{ p: 1.5 }}>
                  <Stack spacing={2}>
                    {siteVisit.calendar_item?.title && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Title
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {siteVisit.calendar_item.title}
                        </Typography>
                      </Box>
                    )}
                    
                    {siteVisit.agent_notes && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Notes
                        </Typography>
                        <Paper sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                          <Typography variant="body2">
                            {siteVisit.agent_notes}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {siteVisit.rescheduling_reason && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {siteVisit.status === 'cancelled' ? 'Cancellation' : 'Rescheduling'} Reason
                        </Typography>
                        <Paper sx={{ p: 1.5, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                          <Typography variant="body2">
                            {siteVisit.rescheduling_reason}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                    
                    {siteVisit.tags?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {siteVisit.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ 
                                borderRadius: 2,
                                fontSize: '0.7rem',
                                '& .MuiChip-label': { px: 0.75 }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Fade>
          </Stack>
        </Grid>

        {/* Right Column - 30% */}
        <Grid item xs={12} lg={3.6}>
          <Stack spacing={2} sx={{ height: '100%', minHeight: '600px' }}>
            {/* Row 1: Agent Card */}
            <Fade in={!!siteVisit} timeout={1000}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardHeader
                  title="Assigned Agent"
                  avatar={<BusinessIcon color="primary" fontSize="small" />}
                  titleTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                />
                <CardContent sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: 'primary.main',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    >
                      {agentInfo?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Button
                        variant="text"
                        sx={{ 
                          p: 0, 
                          textTransform: 'none', 
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          minWidth: 'auto',
                          '&:hover': { bgcolor: 'transparent' }
                        }}
                        onClick={() => navigate(`/agents/${agentInfo?.id}`)}
                      >
                        <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ fontSize: '0.8rem' }}>
                          {agentInfo?.name || 'Not Assigned'}
                        </Typography>
                      </Button>
                    </Box>
                  </Box>
                  
                  {agentInfo?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {agentInfo.email}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Fade>

            {/* Row 2: History */}
            <Fade in={!!siteVisit} timeout={1200}>
              <Card sx={{ 
                borderRadius: 2, 
                boxShadow: 2, 
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
              }}>
                <CardHeader
                  title="Timeline"
                  avatar={<HistoryIcon color="primary" fontSize="small" />}
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                />
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', p: 1.5 }}>
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f8f9fa',
                    borderRadius: '5px',
                    margin: '4px 0',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '5px',
                    border: '2px solid #f8f9fa',
                    '&:hover': {
                      background: '#a8a8a8',
                    },
                  },
                  '&::-webkit-scrollbar-corner': {
                    background: '#f8f9fa',
                  },
                }}>
                  <Stack spacing={1}>
                    {reschedulingHistory.map((item, index) => (
                    <Box key={item.id} sx={{ position: 'relative' }}>
                      {/* Timeline line */}
                      {index < reschedulingHistory.length - 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 16,
                            top: 32,
                            width: 2,
                            height: 'calc(100% + 12px)',
                            bgcolor: 'primary.main',
                            zIndex: 1
                          }}
                        />
                      )}
                      
                      {/* Timeline item */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {/* Timeline dot */}
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: item.id === siteVisit?.id ? 'success.main' : 
                                   item.status === 'rescheduled' ? 'warning.main' : 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2,
                            position: 'relative',
                            boxShadow: item.id === siteVisit?.id ? 2 : 1,
                            border: item.id === siteVisit?.id ? '2px solid white' : 'none'
                          }}
                        >
                          {item.id === siteVisit?.id ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                          ) : item.status === 'rescheduled' ? (
                            <EventIcon sx={{ fontSize: 16, color: 'white' }} />
                          ) : (
                            <HistoryIcon sx={{ fontSize: 16, color: 'white' }} />
                          )}
                        </Box>
                        
                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Paper 
                            elevation={item.id === siteVisit?.id ? 2 : 1} 
                            sx={{ 
                              p: 1, 
                              bgcolor: item.id === siteVisit?.id ? 'info.50' : 'white', 
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: item.id === siteVisit?.id ? 'info.light' : 'grey.200',
                              position: 'relative',
                              overflow: 'hidden',
                              boxShadow: item.id === siteVisit?.id ? '0 1px 5px rgba(3, 169, 244, 0.1)' : 'none'
                            }}
                          >
                            {/* Current visit active indicator */}
                            {item.id === siteVisit?.id && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: 3,
                                  bgcolor: 'info.light',
                                  boxShadow: '0 1px 3px rgba(3, 169, 244, 0.2)'
                                }}
                              />
                            )}
                            
                            {/* Title */}
                            <Typography 
                              variant="body2" 
                              fontWeight={item.id === siteVisit?.id ? 600 : 500} 
                              color={item.id === siteVisit?.id ? 'success.dark' : 'text.primary'}
                              sx={{ mb: 0.5, fontSize: item.id === siteVisit?.id ? '0.9rem' : '0.8rem' }}
                            >
                              {item.calendar_item?.title || 'Site Visit'}
                              {item.id === siteVisit?.id && (
                                <Chip
                                  label="ACTIVE"
                                  size="small"
                                  color="success"
                                  sx={{ 
                                    ml: 0.5, 
                                    fontSize: '0.6rem',
                                    height: 16,
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Typography>
                            
                            {/* Date and Time Pills */}
                            <Box sx={{ display: 'flex', gap: 0.25, mb: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={formatDate(item.calendar_item?.start_time)}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  bgcolor: 'grey.50',
                                  borderColor: 'grey.300',
                                  fontSize: '0.65rem',
                                  height: 18
                                }}
                              />
                              <Chip
                                label={formatTime(item.calendar_item?.start_time)}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  bgcolor: 'grey.50',
                                  borderColor: 'grey.300',
                                  fontSize: '0.65rem',
                                  height: 18
                                }}
                              />
                            </Box>
                            
                            {/* Agent */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.5 }}>
                              <PersonIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" fontWeight={500} color="text.primary" sx={{ fontSize: '0.7rem' }}>
                                {item.calendar_item?.agent || 'Unassigned'}
                              </Typography>
                            </Box>
                            
                            {/* Visit Type | Travel Type */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.5 }}>
                              {transportInfo.icon && React.cloneElement(transportInfo.icon, { fontSize: 'small' })}
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {item.visit_type_verbose} | {item.transportation_type_verbose || 'Self'}
                              </Typography>
                            </Box>
                            
                            {/* Reason */}
                            {item.rescheduling_reason && (
                              <Box sx={{ 
                                p: 0.75, 
                                bgcolor: 'grey.50', 
                                borderRadius: 1.5,
                                border: '1px solid grey.200'
                              }}>
                                <Typography variant="caption" fontWeight={500} color="text.primary" sx={{ mb: 0.25, fontSize: '0.7rem' }}>
                                  Reason:
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                  {item.rescheduling_reason}
                                </Typography>
                              </Box>
                            )}
                          </Paper>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
              {reschedulingHistory.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <HistoryIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No rescheduling history available
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Stack>
      </Grid>
    </Grid>
  </Container>
</>
);
};

export default SiteVisitDetails;